import { useEffect, useRef, useState } from 'react';

/**
 * The event hero: a still image, optionally replaced by a short looping video.
 *
 * THE IMAGE IS THE TRUTH, THE VIDEO IS AN UPGRADE. The image renders first, always, and
 * is never unmounted. The video is layered over it and only becomes visible once it is
 * genuinely playing. So the failure mode is not "broken video", it is "the page you
 * would have had anyway", and there is no state in which a visitor sees a spinner, a
 * black box, or a control bar.
 *
 * NO LAYOUT SHIFT BY CONSTRUCTION. The parent is a fixed 21:9 box and the video is
 * absolutely positioned inside it. Nothing reflows when the video appears, because
 * nothing about the box depends on whether it is there.
 *
 * "STARTED" MEANS FRAMES ARE MOVING. canplay only says enough data has arrived, and a
 * video can fire it and then stall. The test here is that the element is unpaused, past
 * readyState 3, and has a currentTime above zero, which cannot be true unless it is
 * actually rendering. If that is not true within the deadline, the attempt is abandoned
 * and the source is torn down so the download stops rather than continuing to spend a
 * visitor's data on something they will never see.
 */

/** How long a video gets to start before the attempt is quietly dropped. */
const START_DEADLINE_MS = 1800;

interface Props {
  imageUrl: string | null;
  videoUrl?: string | null;
  videoWebmUrl?: string | null;
}

/**
 * Should we even try?
 *
 * Read once, at mount. Both signals are user settings rather than page state, and a
 * video that vanished mid-scroll because a setting flipped would be stranger than one
 * that never started.
 */
function shouldAttemptVideo(sources: Array<string | null | undefined>): boolean {
  // ANY source is enough. This used to gate on the MP4 alone, which meant an event with
  // only a WebM URL never mounted a <video> at all, so the <source type="video/webm">
  // below never got a chance to play. MP4 is the format relied on for coverage, which is
  // a reason to prefer it, not a reason to require it.
  if (!sources.some((s) => typeof s === 'string' && s.trim() !== '')) return false;
  if (typeof window === 'undefined') return false;

  // A real accessibility setting. Autoplaying motion is exactly what it asks us not to do.
  if (window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches) return false;

  // Data Saver, where the browser exposes it. Chromium-family only today; absence of the
  // API is not consent, but it is also not a refusal, so we proceed.
  const conn = (navigator as Navigator & {
    connection?: { saveData?: boolean };
  }).connection;
  if (conn?.saveData === true) return false;

  return true;
}

export function HeroMedia({ imageUrl, videoUrl, videoWebmUrl }: Props) {
  const [attempt] = useState(() => shouldAttemptVideo([videoUrl, videoWebmUrl]));
  const [playing, setPlaying] = useState(false);
  const videoRef = useRef<HTMLVideoElement | null>(null);

  useEffect(() => {
    if (!attempt) return undefined;
    const video = videoRef.current;
    if (!video) return undefined;

    let settled = false;

    const started = () =>
      !video.paused && video.readyState >= 3 && video.currentTime > 0;

    const accept = () => {
      if (settled || !started()) return;
      settled = true;
      window.clearTimeout(timer);
      setPlaying(true);
    };

    /** Give up silently and stop the download. */
    const abandon = () => {
      if (settled) return;
      settled = true;
      setPlaying(false);
      try {
        video.pause();
        // Detaching the sources is what actually cancels the transfer. Without it the
        // browser keeps pulling a video nobody will see.
        video.removeAttribute('src');
        while (video.firstChild) video.removeChild(video.firstChild);
        video.load();
      } catch {
        // Nothing here is worth surfacing: the image is already on screen.
      }
    };

    const timer = window.setTimeout(abandon, START_DEADLINE_MS);

    video.addEventListener('playing', accept);
    video.addEventListener('timeupdate', accept);
    video.addEventListener('error', abandon);
    video.addEventListener('stalled', abandon);

    // Autoplay can still be refused despite muted+playsInline. A rejected promise is a
    // normal outcome, not an error to report.
    void video.play?.().catch(() => abandon());

    return () => {
      window.clearTimeout(timer);
      video.removeEventListener('playing', accept);
      video.removeEventListener('timeupdate', accept);
      video.removeEventListener('error', abandon);
      video.removeEventListener('stalled', abandon);
    };
  }, [attempt]);

  return (
    <>
      {imageUrl ? (
        <img src={imageUrl} alt="" />
      ) : (
        <div className="detail__hero-fallback" aria-hidden="true" />
      )}

      {attempt && (
        <video
          ref={videoRef}
          className={`detail__hero-video${playing ? ' is-playing' : ''}`}
          // playsInline is what allows inline autoplay on iOS Safari. Without it the
          // video takes over the screen in a native player, which is not the feature.
          playsInline
          muted
          loop
          autoPlay
          preload="auto"
          poster={imageUrl ?? undefined}
          // Decorative. The event has a title and a description; a silent loop adds
          // nothing for a screen reader and would only interrupt.
          aria-hidden="true"
          tabIndex={-1}
        >
          {/* Browsers take the FIRST source they can decode, so WebM leads for engines
              that prefer it and Safari falls through to the MP4. Either alone is enough:
              whichever is supplied is the one that plays. */}
          {videoWebmUrl && <source src={videoWebmUrl} type="video/webm" />}
          {videoUrl && <source src={videoUrl} type="video/mp4" />}
        </video>
      )}
    </>
  );
}
